import { Controller } from "@hotwired/stimulus"
import { debug } from "../utils/debug"

/**
 * Product Page Controller
 *
 * Handles dynamic loading of product-specific data (price, stock, shipping, CTA)
 * from a consolidated JSON endpoint. This allows the page to be cached at the
 * CDN level while still displaying real-time inventory and pricing.
 *
 * Data is fetched from `/product_data.json` and distributed to:
 * - #price-info - Price display with optional discount info
 * - #stock-status - Stock availability and shipping info
 * - #main-call-to-action - Add to cart or out-of-stock CTA
 *
 * @controller product-page
 * @value {String} sku - Product SKU for tracking/debugging
 * @value {String} dataUrl - URL to fetch product data JSON
 *
 * @example Product page with dynamic content
 * <main data-controller="product-page"
 *       data-product-page-sku-value="TRT-120-3X5"
 *       data-product-page-data-url-value="/en-US/products/code/TRT-120-3X5/product_data">
 *
 *   <div id="price-info"><!-- Price loaded here --></div>
 *   <div id="stock-status"><!-- Stock info loaded here --></div>
 *   <div id="main-call-to-action"><!-- CTA loaded here --></div>
 * </main>
 *
 * @notes
 * - Uses element-level data attributes to prevent duplicate loading on Turbo navigation
 * - Listens for turbo:before-stream-render to handle variant switches
 * - Clears cart/quantity controller flags before CTA replacement
 *
 * @see tooltip controller for Bootstrap tooltips
 * @see popover controller for Bootstrap popovers
 * @see @stimulus-components/scroll-to for hash navigation
 *
 * @used-in app/views/www/products/_sku_body.html.erb
 * @used-in app/views/www/products/_service_body.html.erb
 */
export default class extends Controller {
  static values = {
    sku: String,
    dataUrl: String
  }

  connect() {
    debug("product-page", `connect() SKU: ${this.skuValue}`, {
      loaded: this.element.dataset.productPageLoaded,
      loading: this.element.dataset.productPageLoading
    })

    // Use element-level data attribute to prevent duplicate loading across reconnections
    // This persists even if Stimulus creates a new controller instance
    if (this.element.dataset.productPageLoaded === "true") {
      debug("product-page", "Skipping - already loaded")
      return
    }
    if (this.element.dataset.productPageLoading === "true") {
      debug("product-page", "Skipping - already loading")
      return
    }

    this.loadDynamicContent()

    // Listen for Turbo Stream updates to reinitialize dynamic content
    document.addEventListener("turbo:before-stream-render", this.handleBeforeStreamRender)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.handleBeforeStreamRender)
  }

  handleBeforeStreamRender = (event) => {
    // When turbo stream replaces product-content, just clear flags
    // The NEW controller's connect() will handle loading for the new variant
    const stream = event.target
    const targetId = stream?.getAttribute?.("target")
    debug("product-page", `turbo:before-stream-render target: ${targetId}`)

    if (targetId === "product-content") {
      // Clear flags - the new controller instance will load on connect()
      delete this.element.dataset.productPageLoaded
      delete this.element.dataset.productPageLoading
      debug("product-page", "Cleared flags for variant switch (new controller will load)")
    }
  }

  /**
   * Load dynamic content from consolidated endpoint
   * Single fetch for all product data: price, stock, shipping, CTA state
   */
  async loadDynamicContent() {
    // Prevent duplicate calls - use element-level flags for cross-instance safety
    if (this.element.dataset.productPageLoading === "true") {
      debug("product-page", "loadDynamicContent() - skipping, already loading")
      return
    }
    if (this.element.dataset.productPageLoaded === "true") {
      debug("product-page", "loadDynamicContent() - skipping, already loaded")
      return
    }

    this.element.dataset.productPageLoading = "true"
    debug("product-page", `loadDynamicContent() - starting fetch for ${this.skuValue}`)

    try {
      // Try consolidated endpoint first, fall back to legacy if needed
      const dataUrl = this.dataUrlValue || this.element.dataset.productDataUrl

      if (dataUrl) {
        debug("product-page", `Fetching: ${dataUrl}`)
        await this.loadFromConsolidatedEndpoint(dataUrl)
      } else {
        // Fallback to legacy endpoints if no consolidated URL
        debug("product-page", "Using legacy endpoints")
        await this.loadFromLegacyEndpoints()
      }

      this.element.dataset.productPageLoaded = "true"
      debug("product-page", "Load complete, marked as loaded")
    } catch (error) {
      debug.error("product-page", "Failed to load dynamic content:", error)
    } finally {
      delete this.element.dataset.productPageLoading
    }
  }

  /**
   * Load from single consolidated endpoint (preferred)
   */
  async loadFromConsolidatedEndpoint(url) {
    const response = await this.fetchWithCredentials(url, { Accept: "application/json" })

    if (!response.ok) {
      debug.warn('product-page', `Consolidated endpoint returned ${response.status}`)
      return
    }

    const data = await response.json()

    // Distribute data to DOM elements
    this.updatePriceDisplay(data.price)
    this.updateShippingDisplay(data.shipping)
    this.updateStockDisplay(data.stock)
    this.updateCtaDisplay(data.cta)
  }

  /**
   * Fallback to legacy separate endpoints
   */
  async loadFromLegacyEndpoints() {
    await Promise.all([
      this.loadProductInfoLegacy(),
      this.loadCtaOptionsLegacy()
    ])
  }

  /**
   * Legacy: Load CTA options from separate endpoint
   */
  async loadCtaOptionsLegacy() {
    const element = document.getElementById("main-call-to-action")
    if (!element) return

    // Legacy pages may have a data attribute with the URL
    const url = element.dataset.ctaOptionsUrl
    if (!url) return

    // Skip if already loaded (has content other than whitespace)
    if (element.innerHTML.trim()) return

    try {
      const response = await this.fetchWithCredentials(url)
      if (response.ok) {
        element.innerHTML = await response.text()
      }
    } catch (error) {
      debug.warn('product-page', 'Failed to load CTA options:', error)
    }
  }

  /**
   * Update price display with new data
   */
  updatePriceDisplay(price) {
    if (!price) return

    const priceElement = document.getElementById("price-info")
    if (!priceElement) return

    // Build price HTML based on discount state
    let html
    if (price.is_discounted) {
      html = `
        <div class="price-callout d-flex align-items-start flex-wrap gap-2 mt-2">
          <span class="current-price display-6 fw-bold text-black me-2">${price.formatted}</span>
          <div class="d-flex flex-column small text-muted me-3">
            <span>Was <del class="text-muted">${price.original_formatted}</del></span>
            <button class="btn ws-normal lh-3 py-0 px-0 text-start product-cta d-inline-flex align-items-center gap-2 text-success fw-semibold" type="button" data-bs-toggle="modal" data-bs-target="#modal-product-discount-terms" style="border: none; background: none; padding: 0;">
              <i class="fa-sharp fa-solid fa-tags text-danger"></i>
              <span>Save ${price.savings_formatted} (${price.discount_percentage}%)</span>
            </button>
          </div>
        </div>
      `
    } else {
      html = `
        <div class="price-callout d-flex align-items-start flex-wrap gap-2 mt-2">
          <span class="current-price display-6 fw-bold text-black me-2">${price.formatted}</span>
        </div>
      `
    }

    priceElement.innerHTML = html
  }

  /**
   * Update shipping display with new data
   * Note: This is now handled by updateStockDisplay which provides richer info
   * This method is kept for legacy endpoint compatibility only
   */
  updateShippingDisplay(shipping) {
    // Skip if stock-status element exists - stock display handles this now
    if (document.getElementById("stock-status")) return
    if (!shipping?.text) return

    const titleSpan = document.querySelector("#modal-shipping-button > span.title")
    if (titleSpan) {
      // Update only the text node, preserving any icons
      const textNodes = Array.from(titleSpan.childNodes).filter(
        node => node.nodeType === Node.TEXT_NODE
      )
      if (textNodes.length > 0) {
        textNodes[0].textContent = shipping.text
      }
    }

    // Update icon if present
    const iconElement = document.querySelector("#modal-shipping-button > i")
    if (iconElement && shipping.icon) {
      iconElement.className = shipping.icon
    }
  }

  /**
   * Update stock-related displays with tiered messaging
   */
  updateStockDisplay(stock) {
    if (!stock) return

    // Update quantity input max if refurbished (limited stock)
    if (stock.quantity != null) {
      const qtyInput = document.querySelector(".input-group-qty input[type=number]")
      if (qtyInput) {
        qtyInput.dataset.maxAvailable = stock.quantity
      }
    }

    // Update consolidated stock status display (inside shipping button)
    const stockStatusElement = document.getElementById("stock-status")
    if (stockStatusElement && stock.message) {
      this.renderStockStatus(stockStatusElement, stock)
    }
  }

  /**
   * Render tiered stock status with visual indicators
   * This renders inside the shipping modal button for a single consolidated display
   */
  renderStockStatus(element, stock) {
    const iconClass = stock.icon || "fa-circle-check"
    const colorClass = stock.color_class || "text-success"
    const urgencyClass = stock.show_urgency ? "stock-urgency" : ""

    let html = ""

    if (stock.available) {
      // In stock - show icon + message
      html = `
        <i class="fa-sharp fa-solid ${iconClass} ${colorClass} me-1"></i>
        <span class="${urgencyClass}">${stock.message}</span>
        <i class="fa-sharp fa-solid fa-angle-right fa-xs ms-1"></i>
      `
    } else {
      // Out of stock - message already includes expected date if available
      html = `
        <i class="fa-sharp fa-solid fa-exclamation-triangle text-warning me-1"></i>
        <span class="text-muted">${stock.message}</span>
        <i class="fa-sharp fa-solid fa-angle-right fa-xs ms-1"></i>
      `
    }

    element.innerHTML = html
  }

  /**
   * Update CTA (Call to Action) display
   * Renders server-provided HTML for add to cart, out of stock, etc.
   *
   * Progressive enhancement: The page renders add_to_cart by default (works without JS).
   * This method only replaces the CTA if the real-time data indicates a different state
   * (out_of_stock, pro_exclusive, where_to_buy, unavailable).
   */
  updateCtaDisplay(cta) {
    if (!cta?.html) return

    const element = document.getElementById("main-call-to-action")
    if (!element) return

    // Progressive enhancement: only replace if CTA type differs from default
    // Default is 'add_to_cart' (rendered server-side for no-JS fallback)
    const defaultCta = element.dataset.defaultCta || 'add_to_cart'
    if (cta.type === defaultCta) {
      debug('product-page', `CTA type matches default (${defaultCta}), keeping server-rendered HTML`)
      return
    }

    debug('product-page', `CTA type changed: ${defaultCta} -> ${cta.type}, updating HTML`)
    element.innerHTML = cta.html
  }

  /**
   * Legacy: Load product info from separate endpoints
   */
  async loadProductInfoLegacy() {
    const statusElement = document.getElementById("product-status")
    const priceElement = document.getElementById("price-info")

    if (!statusElement && !priceElement) return

    const statusUrl = statusElement?.dataset?.productStatusUrl
    const priceUrl = priceElement?.dataset?.priceInfoUrl

    if (!statusUrl && !priceUrl) return

    try {
      const promises = []

      if (statusUrl) {
        promises.push(
          this.fetchWithCredentials(statusUrl, { Accept: "application/json" })
            .then(async response => {
              if (!response.ok) return null
              const text = await response.text()
              if (!text) return null
              return { type: 'status', data: JSON.parse(text) }
            })
            .catch(error => {
              debug.warn('product-page', 'Failed to load product status:', error)
              return null
            })
        )
      }

      if (priceUrl) {
        promises.push(
          this.fetchWithCredentials(priceUrl)
            .then(async response => {
              if (!response.ok) return null
              return { type: 'price', html: await response.text() }
            })
            .catch(error => {
              debug.warn('product-page', 'Failed to load price info:', error)
              return null
            })
        )
      }

      const results = await Promise.all(promises)

      for (const result of results) {
        if (!result) continue

        if (result.type === 'status' && result.data) {
          this.updateShippingDisplay(result.data.shipping)
        } else if (result.type === 'price' && result.html && priceElement) {
          priceElement.innerHTML = result.html
        }
      }

    } catch (error) {
      debug.warn('product-page', 'Failed to load product info:', error)
    }
  }

  async fetchWithCredentials(url, additionalHeaders = {}) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return fetch(url, {
      credentials: "include",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken || "",
        ...additionalHeaders
      }
    })
  }
}
